import { type CampaignUtm, readCampaignUtm } from "./landing-attribution";

/**
 * Funnel ids live in memory only: nothing on the device, so no consent (§ 25 TDDDG).
 * Full reload = new visitor.
 */
const IDLE_MS: number = 30 * 60 * 1000;

/** Ids older builds persisted without consent; removed on first use. */
const STALE_LOCAL_KEYS: readonly string[] = [
  "hv_vid",
  "hv_seen",
  "careers-apply-visitor",
  "demo-lead-visitor",
];
const STALE_SESSION_KEYS: readonly string[] = ["hv_sid"];

const memory = {
  visitor: null as string | null,
  session: null as string | null,
  lastSeen: 0,
  staleCleared: false,
};

function clearStaleStorage(): void {
  if (memory.staleCleared) return;
  memory.staleCleared = true;
  try {
    for (const key of STALE_LOCAL_KEYS) window.localStorage.removeItem(key);
    for (const key of STALE_SESSION_KEYS) window.sessionStorage.removeItem(key);
  } catch {
    // Storage blocked: nothing to clear.
  }
}

/** Anonymous id for this page life; also the demo/careers rate-limit key. */
export function getOrCreateMarketingVisitorId(): string {
  if (typeof window === "undefined") {
    throw new Error("visitor id unavailable during SSR");
  }
  clearStaleStorage();
  memory.visitor = memory.visitor ?? crypto.randomUUID();
  return memory.visitor;
}

/** Session id. New id after 30 min idle; server ignores premature bumps. */
export function getOrCreateMarketingSessionId(): string {
  if (typeof window === "undefined") {
    throw new Error("session id unavailable during SSR");
  }
  clearStaleStorage();
  const now: number = Date.now();
  if (memory.session === null || now - memory.lastSeen >= IDLE_MS) {
    memory.session = crypto.randomUUID();
  }
  memory.lastSeen = now;
  return memory.session;
}

export function readMarketingUtm(): string | undefined {
  const utm: CampaignUtm | undefined = readCampaignUtm();
  if (utm === undefined) return undefined;
  const parts: string[] = [utm.utm_source, utm.utm_medium, utm.utm_campaign]
    .filter((value): value is string => value !== undefined);
  if (parts.length === 0) return undefined;
  return parts.join("|").slice(0, 200);
}
